import { Info, X } from 'lucide-react';
import { useState } from 'react';
import { TenantSelector } from '../../../../components/tenant-selector/TenantSelector';
import type {
  TenantSelectionConflicts,
  TenantSelectorRowData,
} from '../../../../components/tenant-selector/types';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import type { TenantSelectionType } from '../../engage-automations-tab/templates.types';
import { useAutomationEditorContext } from '../AutomationEditorContext';
import { useEditorSyncStatus } from '../EditorSyncStatusContext';
import styles from './TenantSelectionControl.module.css';
import { TenantSelectionHelpPanel } from './TenantSelectionHelpPanel';
import { TenantSelectionValidationStatus } from './TenantSelectionValidationStatus';

interface TenantSelectionDialogProps {
  onClose: () => void;
  tenantRowData: Record<string, TenantSelectorRowData>;
  conflicts: TenantSelectionConflicts | null;
  isValidating: boolean;
  isLoading?: boolean;
}

export function TenantSelectionDialog({
  onClose,
  tenantRowData,
  conflicts,
  isValidating,
  isLoading,
}: TenantSelectionDialogProps) {
  const { editor } = useAutomationEditorContext();
  const { state, updateTenantSelection } = editor;
  const disabled = useEditorSyncStatus();
  const { tenantSelection } = state;

  // Snapshotted on mount; the parent conditionally renders this dialog, so each open starts fresh.
  const [localSelectionType, setLocalSelectionType] = useState<TenantSelectionType>(
    tenantSelection.selectionType,
  );
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(tenantSelection.selectedTenantIds);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleLocalChange = (type: TenantSelectionType, ids: string[]) => {
    setLocalSelectionType(type);
    setLocalSelectedIds(ids);
  };

  const handleConfirm = () => {
    updateTenantSelection(localSelectionType, localSelectedIds);
    onClose();
  };

  const showConflicts = localSelectionType === 'selected' && localSelectedIds.length > 0 && conflicts;

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <hgroup>
            <h2>Tenant Selection</h2>
            <p>Choose which tenants receive this automation when you save.</p>
          </hgroup>
          <button
            type="button"
            onClick={() => setHelpOpen(open => !open)}
            className={styles.helpButton}
            aria-expanded={helpOpen}>
            <Info aria-hidden="true" />
            {helpOpen ? 'Hide explanation' : 'Learn more'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close tenant selection">
            <X />
          </button>
        </header>

        <main>
          {helpOpen && <TenantSelectionHelpPanel context="automation" />}
          <TenantSelector
            tenantSelectionType={localSelectionType}
            selectedTenantIds={localSelectedIds}
            onChange={handleLocalChange}
            includeDraftOption
            draftSelectionDescription="Not synced to anyone yet. Keep building — nobody receives it until you sync it to tenants."
            allTenantsSelectionDescription="New tenants will automatically receive this automation when they're added."
            selectedTenantsSelectionDescription="Only the tenants you select below will receive this automation. New tenants will not receive it unless you add them."
            disabled={disabled}
            tenantRowData={tenantRowData}
            isLoading={isLoading}
          />

          {showConflicts && (
            <TenantSelectionValidationStatus conflicts={conflicts} isValidating={isValidating} />
          )}
        </main>

        <footer className={styles.dialogFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} className={styles.confirmButton}>
            OK
          </button>
        </footer>
      </dialog>
    </>
  );
}
